import React, { useEffect, useCallback, useState, isValidElement } from 'react';
import '../Styles/InformationDialog.scss';

const InformationDialog = ({ isOpen, title, information, handleClose }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);

  const handleCopy = async () => {
    try {
      // Convertir le contenu en texte brut
      let textToCopy = '';
      if (typeof information === 'string') {
        textToCopy = information;
      } else if (isValidElement(information)) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = information.props?.children || '';
        textToCopy = tempDiv.textContent || tempDiv.innerText || '';
      } else {
        textToCopy = String(information);
      }

      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);

      const textArea = document.createElement('textarea');
      textArea.value = typeof information === 'string' ? information : String(information);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const shouldShowCopyButton = ['vic/page-6', 'rj/observations'].some(path => window.location.href.toLowerCase().includes(path));

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="info-dialog-overlay" onClick={handleBackdropClick}>
      <div className="info-dialog-container">
        <div className="info-dialog-header">
          <div className="info-dialog-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h2 className="info-dialog-title">{title}</h2>
          <button
            className="info-dialog-close"
            onClick={handleClose}
            aria-label="Fermer"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="info-dialog-content">
          <div className="info-dialog-information">
            {typeof information === 'string'
              ? information.split('\n').map((line, index) => (
                <p key={index} className="info-paragraph">
                  {line}
                </p>
              ))
              : information
            }
          </div>
        </div>

        <div className="info-dialog-actions">
          {shouldShowCopyButton && information !== "Aucun code du travail n'a été sélectionné" && (
            <button
              className="info-dialog-button info-dialog-button--secondary"
              onClick={handleCopy}
              title={isCopied ? "Copié !" : "Copier le contenu"}
            >
              {isCopied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="button-icon">
                    <path
                      d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
                      fill="currentColor"
                    />
                  </svg>
                  Copié !
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="button-icon">
                    <path
                      d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"
                      fill="currentColor"
                    />
                    <path
                      d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"
                      fill="currentColor"
                    />
                  </svg>
                  Copier
                </>
              )}
            </button>
          )}
          <button
            className="info-dialog-button info-dialog-button--primary"
            onClick={handleClose}
            autoFocus
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default InformationDialog;